import { Field, Form, Formik } from "formik";
import { isEmpty } from "lodash";
import Button from "pages/_components/Button";
import GridTable from "pages/_components/GridTable/GridTable";
import Notification from "pages/_components/Notification";
import TextField from "pages/_components/fields/TextField";
import AmountField from "pages/_components/fields/formik/AmountField";
import ProductSelector from "pages/_components/fields/formik/FormikProductSelector";
import Selector from "pages/_components/fields/formik/Selector";
import { bool } from "prop-types";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import * as i18n from "util/i18n";
import MainContainer from "pages/_components/MainContainer";
import ResponsiveHeader, { DesktopButtons, MobileButtons } from "pages/_components/header";
import useSchema from "hooks/useSchema";
import * as Yup from "yup";
import useTransferAccounts from "pages/transfers/hooks/useTransferAccounts";
import { actions as comexActions } from "reducers/comex";
import { selectors as sessionSelectors } from "reducers/session";
import { store } from "store";
import CustomText from "pages/_components/CustomText/CustomText";
import Drawer from "pages/_components/Drawer/Drawer";
import Image from "pages/_components/Image";
import I18n from "pages/_components/I18n";
import SwitchField from "pages/_components/fields/formik/SwitchField";
import Checkbox from "pages/_components/Checkbox";
import { Link } from "react-router-dom";
import { push } from "connected-react-router";
import Action from "pages/_components/Action";
import * as configUtils from "util/config";
import { useAccount } from "hooks/useAccounts";
import ComexStepper from "./_components/ComexStepper";

const FORM_ID = "comex.transfer";

const Step3Comex = ({ isDesktop }) => {
    const dispatch = useDispatch();

    const { comexTransfer = {}, comexCurrencies = [], comexId = null } = useSelector((state) => state.comex);
    const { transferData = {}, concept } = comexTransfer;
    const { stringRequired, numberRequired, stringNullableRequired } = useSchema();
    const { debitAccountOptions, commissionsAccount, fetching, accounts } = useTransferAccounts("comexTransfers");
    const [showOriginAccountInfo, setShowOriginAccountInfo] = useState(false);
    const [showExpensesInfo, setShowExpensesInfo] = useState(false);
    const [isCommissionsDisabled, setIsCommissionsDisabled] = useState(false);
    const [isSaved, setIsSaved] = useState(false);

    const debitAccountOptionsFiltered = debitAccountOptions.filter((obj) => !obj.label.includes("CA U$D"));
    const [prefixValue, setPrefixValue] = useState();
    const [isSaveEnabled, setIsSaveEnabled] = useState(false);
    const [currencyCode, setCurrencyCode] = useState("");

    const comexInstrumentsConfig = configUtils.getArray("comex.instruments");
    const comexInstruments = i18n.formatOptionLabel(comexInstrumentsConfig, `${FORM_ID}.instrument`);
    const soldInstrumentCode = configUtils.getArray("comex.sold.instrument");

    useEffect(() => {
        dispatch(comexActions.getComexSelectors());

        if (
            comexTransfer?.transferData?.originAccount != null &&
            comexTransfer?.transferData?.amountField?.currency != null &&
            comexTransfer?.transferData?.amountField?.currency == "ARS"
        ) {
            setIsCommissionsDisabled(true);
        }
    }, []);

    useEffect(() => {
        if (transferData.currency) {
            setCurrencyCode(transferData.currency);
        }
    }, [transferData.currency]);

    const { selectedAccount: _debitAccount, formatAccountBody } = useAccount("productId", transferData?.originAccount);

    const saveComexRequest = (isContinue) => {
        const _comexTransferRequest = {
            ...comexTransfer,
            step: "stepORIGINACCOUNT",
            comexId,
            isContinue,
        };
        dispatch(comexActions.createComexTransfer(_comexTransferRequest));
        setIsSaved(true);
    };

    const handleOriginAccount = (debitAccountId, setFieldValue = null, values) => {
        const selected = accounts.find((_account) => _account.productId === debitAccountId);
        if (setFieldValue) {
            setFieldValue("amountField.currency", selected.currency);
            if (selected.currency === "ARS") {
                setFieldValue("commissionAccount", debitAccountId);
                setIsCommissionsDisabled(true);
            } else {
                setIsCommissionsDisabled(false);
                setFieldValue("commissionAccount", "");
            }
        }
        dispatch(
            comexActions.setComexTransfer({
                ...comexTransfer,
                transferData: {
                    ...values,
                    amountField: { ...values?.amountField, currency: selected?.currency },
                    originAccount: selected?.productId,
                },
            }),
        );
    };

    const currencies = [
        { id: "ARS", label: i18n.get(`currency.label.ARS`) },
        { id: "USD", label: i18n.get(`currency.label.USD`) },
    ];

    const username = sessionSelectors.getUserFullName(store.getState());
    const title = i18n.get("comex.request.title").replace("#{NAME}", username);

    const initialValues = {
        originAccount: transferData?.originAccount || "",
        commissionAccount: transferData?.commissionAccount || "",
        bank: transferData?.bank || false,
        intermediary: transferData?.intermediary || "",
        code: transferData?.code || "",
        country: transferData?.country || "",
        currency: transferData?.currency || "",
        amountField: {
            amount: transferData?.amountField?.amount || null,
            currency: transferData?.amountField?.currency || "ARS",
        },
        reason: transferData?.reason || "",
        originator: transferData?.originator || false,
        beneficiary: transferData?.beneficiary || false,
        both: transferData?.both || false,
        cashCurrencyExchange: transferData?.cashCurrencyExchange || false,
        exchangeSale: transferData?.exchangeSale || false,
        currencyCode: transferData?.currencyCode || "",
        instrumentCode: transferData?.instrumentCode || "",
    };

    const handleOnChangeAmount = (values) => {
        dispatch(
            comexActions.setComexTransfer({
                ...comexTransfer,
                transferData: {
                    ...values,
                    amount: {
                        quantity: values.amountField.amount,
                        currency: values.amountField.currency,
                    },
                },
            }),
        );
    };

    const validationSchema = Yup.object().shape({
        originAccount: stringRequired,
        commissionAccount: stringRequired,
        currency: stringNullableRequired,
        amountField: Yup.object().shape({
            amount: numberRequired,
        }),
        code: Yup.string().when("bank", {
            is: true,
            then: (schema) =>
                schema
                    .matches(/^[a-zA-Z0-9]{8,11}$/, i18n.get("comex.transfer.code.validate.error"))
                    .required(i18n.get("global.error.required")),
            otherwise: (schema) => schema.notRequired(),
        }),
        reason: Yup.string()
            .min(1, "El motivo debe tener al menos 1 carácter.")
            .max(100, "El motivo no puede superar los 100 caracteres.")
            .optional(),
        instrumentCode: stringNullableRequired,
    });

    const handleSubmit = () => {
        saveComexRequest(true);

        dispatch(
            comexActions.setComexTransfer({
                ...comexTransfer,
                transferData: {
                    ...comexTransfer.transferData,
                    originAccountLabel: formatAccountBody(_debitAccount),
                    ownerName: _debitAccount?.ownerName,
                    ownerCuit: _debitAccount?.taxBankCode,
                    ownerCbu: _debitAccount?.uniformBankCode,
                },
            }),
        );

        const isGoods = concept.includes("B");

        if (isGoods) {
            dispatch(push("/comex/clearance"));
        } else {
            dispatch(push("/comex/retention")); // Flujo comun y bienes
        }
    };

    const handleCheckboxChange = (setFieldValue, fieldName) => {
        setFieldValue("originator", fieldName === "originator");
        setFieldValue("beneficiary", fieldName === "beneficiary");
        setFieldValue("both", fieldName === "both");
    };

    const [formData, setFormData] = useState({});

    useEffect(() => {
        dispatch(
            comexActions.setComexTransfer({
                ...comexTransfer,
                transferData: {
                    ...comexTransfer.transferData,
                    ...formData,
                },
            }),
        );

        const requiredFields = ["originAccount", "commissionAccount", "currency", "instrumentCode"];
        if (formData.bank) {
            requiredFields.push("intermediary", "code", "country");
        }

        const isComplete = requiredFields.every((field) => formData?.[field] && formData[field] !== "");

        const group1Valid = formData.originator || formData.beneficiary || formData.both;
        const group2Valid = formData.exchangeSale || formData.cashCurrencyExchange;

        setIsSaveEnabled(isComplete && group1Valid && group2Valid);
        setIsSaved(false);
    }, [formData]);

    const OriginAccountInfo = () => (
        <Drawer
            className="help-slider"
            openSidebar={showOriginAccountInfo}
            toggleSidebar={() => setShowOriginAccountInfo(false)}>
            <div className="container payway-terminal-type-help-container">
                <div
                    className="row"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: i18n.get("comex.origin.account.info.content"),
                    }}
                />
                <p>
                    {i18n.get("comex.origin.account.last.content")}
                    <Link className="link-comissions" to="/comex/comissions">
                        {i18n.get("comex.origin.account.click.content")}
                    </Link>
                </p>
            </div>
        </Drawer>
    );

    const ExpensesResponsibilityInfo = () => (
        <Drawer className="help-slider" openSidebar={showExpensesInfo} toggleSidebar={() => setShowExpensesInfo(false)}>
            <div className="container payway-terminal-type-help-container">
                <div
                    className="row"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: i18n.get("comex.expenses.info.content"),
                    }}
                />
            </div>
        </Drawer>
    );

    return (
        <>
            {isDesktop && <ComexStepper isDesktop={isDesktop} currentStep={3} />}
            <Notification scopeToShow="ComexNotification" />
            <ResponsiveHeader ticket metaTitle="transfers.comex.title" backLinkTo="/comex/payment">
                <DesktopButtons>
                    <Button
                        className="btn btn-outline btn-block small"
                        onClick={() => {
                            saveComexRequest();
                        }}
                        disabled={!isSaveEnabled || isSaved}>
                        <Image
                            src={isSaved ? "images/figma/icon/check.svg" : "images/figma/icon/save.svg"}
                            className="icon-medium svg-icon"
                        />
                        <I18n id={isSaved ? "comex.transfer.save.ok.btn" : "comex.transfer.save.btn"} />
                    </Button>
                </DesktopButtons>
                <MobileButtons>
                    <Action
                        labelKey="comex.transfer.save.btn"
                        handleClick={() => saveComexRequest()}
                        disabled={!isSaveEnabled}
                    />
                </MobileButtons>
            </ResponsiveHeader>

            <OriginAccountInfo />
            <ExpensesResponsibilityInfo />
            <MainContainer showLoader={fetching}>
                <Notification scopeToShow="mepNotification" />
                <h2>{title}</h2>
                <h2>
                    <CustomText
                        text="comex.transfer.origin.account"
                        textType="I18N"
                        showTooltip
                        onTooltipClick={() => setShowOriginAccountInfo(true)}
                    />
                </h2>
                <Formik
                    initialValues={initialValues}
                    onSubmit={() => handleSubmit()}
                    validationSchema={validationSchema}>
                    {({ values, setFieldValue }) => {
                        const validAccount = debitAccountOptions.find((account) => account.id === values.originAccount);
                        const group1Valid = values.originator || values.beneficiary || values.both;
                        const group2Valid = values.exchangeSale || values.cashCurrencyExchange;

                        const canSubmit =
                            !values.originAccount ||
                            !values.commissionAccount ||
                            !values.amountField.amount ||
                            !values.instrumentCode ||
                            (values.bank && (!values.intermediary || !values.code || !values.country)) ||
                            !(group1Valid && group2Valid);

                        isEmpty(validAccount);
                        setPrefixValue(
                            validAccount?.balance?.currency === "ARS"
                                ? i18n.get(`currency.label.ARS`)
                                : i18n.get(`currency.label.USD`),
                        );
                        setFormData(values);
                        return (
                            <Form>
                                <GridTable className="form transfer-others">
                                    <GridTable.Container columns={12} columnsTemplate="fixed">
                                        <GridTable.Data className="required" columnStart={2} columnWidth={10}>
                                            <Field
                                                component={ProductSelector}
                                                label={i18n.get(`${FORM_ID}.originAccount.label`)}
                                                name="originAccount"
                                                placeholder={i18n.get(`${FORM_ID}.originAccount.placeholder`)}
                                                data={{
                                                    emptyOptionLabel: "",
                                                    options: debitAccountOptionsFiltered,
                                                }}
                                                onChange={(id) => handleOriginAccount(id, setFieldValue, values)}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data className="required" columnStart={2} columnWidth={10}>
                                            <Field
                                                component={ProductSelector}
                                                label={i18n.get(`${FORM_ID}.comissions.label`)}
                                                name="commissionAccount"
                                                placeholder={i18n.get(`${FORM_ID}.originAccount.placeholder`)}
                                                data={{
                                                    emptyOptionLabel: "",
                                                    options: commissionsAccount,
                                                }}
                                                disabled={isCommissionsDisabled}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} columnWidth={10}>
                                            <I18n id="comex.payment.intermediary.bank.title" component="h3" />
                                        </GridTable.Data>
                                        <GridTable.Data
                                            columnStart={3}
                                            columnWidth={10}
                                            alignY="flex-start"
                                            className="h-full required">
                                            <Field
                                                component={SwitchField}
                                                idForm={FORM_ID}
                                                name="bank"
                                                aditionalI18nText="comex.transfer.bank.detail.label"
                                                onChange={() => {
                                                    dispatch(
                                                        comexActions.setComexTransfer({
                                                            ...comexTransfer,
                                                            transferData: {
                                                                ...comexTransfer.transferData,
                                                                ...values,
                                                                bank: !values.bank,
                                                            },
                                                        }),
                                                    );

                                                    if (values.bank) {
                                                        setFieldValue("intermediary", "");
                                                        setFieldValue("code", "");
                                                        setFieldValue("country", "");
                                                    }
                                                }}
                                            />
                                        </GridTable.Data>
                                        {values.bank && (
                                            <>
                                                <GridTable.Data columnStart={3} columnWidth={4}>
                                                    <Field
                                                        component={TextField}
                                                        idForm={FORM_ID}
                                                        name="intermediary"
                                                        hidePlaceholder
                                                    />
                                                </GridTable.Data>

                                                <GridTable.Data columnStart={7} columnWidth={4}>
                                                    <Field
                                                        component={TextField}
                                                        idForm={FORM_ID}
                                                        name="code"
                                                        hidePlaceholder
                                                    />
                                                </GridTable.Data>

                                                <GridTable.Data columnStart={3} columnWidth={4}>
                                                    <Field
                                                        component={TextField}
                                                        idForm={FORM_ID}
                                                        name="country"
                                                        hidePlaceholder
                                                    />
                                                </GridTable.Data>
                                            </>
                                        )}
                                        <GridTable.Data className="required" columnStart={2} columnWidth={5}>
                                            <Field
                                                component={Selector}
                                                idForm={FORM_ID}
                                                name="currency"
                                                options={comexCurrencies.map(({ id, description }) => ({
                                                    value: id,
                                                    label: description,
                                                }))}
                                                onChange={(id) => setCurrencyCode(id)}
                                                placeholder=" "
                                                value={null}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data className="required" columnStart={7} columnWidth={5}>
                                            <Field
                                                component={AmountField}
                                                labelText={i18n.get(`${FORM_ID}.amount.label`)}
                                                name="amountField"
                                                disableNumber={!values.originAccount}
                                                data={{ options: currencies }}
                                                clearable={false}
                                                integerLength={10}
                                                decimalLength={2}
                                                onBlurChange={() => handleOnChangeAmount(values, setFieldValue)}
                                                withValueCap
                                                disableSelect
                                                fixedDecimalScale
                                                hideCurrency
                                                prefix={prefixValue}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data flexDirection="column" columnStart={2} columnWidth={5}>
                                            <CustomText
                                                className="data-label text-config-title"
                                                text={`${FORM_ID}.currencyCode.label`}
                                                textType="I18N"
                                            />
                                            <div className="flex gap-6 indent-20 text-config">{currencyCode}</div>
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={7} columnWidth={5}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="reason"
                                                hidePlaceholder
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data flexDirection="column" columnStart={2} columnWidth={10}>
                                            <CustomText
                                                className="data-label comex-subtitle"
                                                text={`${FORM_ID}.expenses.responsibility`}
                                                textType="I18N"
                                                showTooltip
                                                onTooltipClick={() => setShowExpensesInfo(true)}
                                            />

                                            <div
                                                className={`flex ${
                                                    isDesktop ? "flex-row gap-13" : "flex-col gap-6"
                                                } indent-20`}>
                                                <Field
                                                    component={Checkbox}
                                                    idForm={FORM_ID}
                                                    name="originator"
                                                    labelTextProps={{ defaultValue: "originator" }}
                                                    style={{ marginRight: "16px" }}
                                                    onChange={() => handleCheckboxChange(setFieldValue, "originator")}
                                                />
                                                <Field
                                                    component={Checkbox}
                                                    idForm={FORM_ID}
                                                    name="beneficiary"
                                                    labelTextProps={{ defaultValue: "beneficiary" }}
                                                    onChange={() => handleCheckboxChange(setFieldValue, "beneficiary")}
                                                />
                                                <Field
                                                    component={Checkbox}
                                                    idForm={FORM_ID}
                                                    name="both"
                                                    labelTextProps={{ defaultValue: "both" }}
                                                    onChange={() => handleCheckboxChange(setFieldValue, "both")}
                                                />
                                            </div>
                                        </GridTable.Data>
                                        <GridTable.Data flexDirection="column" columnStart={2} columnWidth={10}>
                                            <CustomText
                                                className="data-label comex-subtitle"
                                                text={`${FORM_ID}.operationType.label`}
                                                textType="I18N"
                                            />

                                            <div className="flex gap-6 indent-20">
                                                <Field
                                                    component={Checkbox}
                                                    idForm={FORM_ID}
                                                    name="exchangeSale"
                                                    labelTextProps={{ defaultValue: "exchangeSale" }}
                                                    style={{ marginRight: "16px" }}
                                                    onChange={() => setFieldValue("cashCurrencyExchange", false)}
                                                />
                                                <Field
                                                    component={Checkbox}
                                                    idForm={FORM_ID}
                                                    name="cashCurrencyExchange"
                                                    labelTextProps={{ defaultValue: "cashCurrencyExchange" }}
                                                    onChange={() => setFieldValue("exchangeSale", false)}
                                                />
                                            </div>
                                            <br />
                                        </GridTable.Data>
                                        <GridTable.Data className="required" columnStart={2} columnWidth={5}>
                                            <Field
                                                component={Selector}
                                                idForm={FORM_ID}
                                                name="instrumentCode"
                                                options={comexInstruments}
                                                onChange={(id) => setFieldValue("currencyCode", id)}
                                                placeholder=" "
                                                value={null}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data
                                            flexDirection="column"
                                            className="required"
                                            columnStart={7}
                                            columnWidth={5}>
                                            <CustomText
                                                className="data-label text-config-title"
                                                text={`${FORM_ID}.soldInstrumentCode.label`}
                                                textType="I18N"
                                            />
                                            <div className="flex gap-6 indent-20 text-config">
                                                <I18n id={`${FORM_ID}.instrument.${soldInstrumentCode}.label`} />
                                            </div>
                                        </GridTable.Data>
                                    </GridTable.Container>
                                </GridTable>
                                <Row className="form-actions">
                                    <Col xs={12} md={4} mdOffset={4}>
                                        <Button
                                            type="submit"
                                            className="btn-primary"
                                            label="global.continue"
                                            disabled={canSubmit}
                                        />
                                    </Col>
                                </Row>
                            </Form>
                        );
                    }}
                </Formik>
            </MainContainer>
        </>
    );
};

Step3Comex.propTypes = {
    isDesktop: bool.isRequired,
};

export default Step3Comex;
